import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Plus, Save, RotateCw } from "lucide-react";
import { usePlanner } from "@/features/space-designer/lib/store";
import { CATALOG } from "@/features/space-designer/lib/catalog";
import { ItemShape } from "./ItemShape";
import { toast } from "sonner";

interface Props {
  open: boolean;
  roomId: string | null;
  onOpenChange: (v: boolean) => void;
}

const cmToFtIn = (cm: number) => {
  const totalIn = cm / 2.54;
  const ft = Math.floor(totalIn / 12);
  const inch = Math.round(totalIn - ft * 12);
  return `${ft}' ${inch}"`;
};

export function ClusterEditor({ open, roomId, onOpenChange }: Props) {
  const scene = usePlanner((s) => s.scene);
  const updateItem = usePlanner((s) => s.updateItem);
  const removeItem = usePlanner((s) => s.removeItem);
  const addFromCatalog = usePlanner((s) => s.addFromCatalog);
  const saveClusterPreset = usePlanner((s) => s.saveClusterPreset);
  const clearClusterPreset = usePlanner((s) => s.clearClusterPreset);
  const select = usePlanner((s) => s.select);

  const room = roomId ? scene.items.find((i) => i.id === roomId) : null;
  const children = useMemo(
    () => (room?.groupId ? scene.items.filter((i) => i.groupId === room.groupId && i.id !== room.id) : []),
    [scene.items, room],
  );
  const [addId, setAddId] = useState<string>("");
  const [dragging, setDragging] = useState<{ id: string; startX: number; startY: number; origX: number; origY: number } | null>(null);

  if (!room || !room.groupId) return null;

  const PREVIEW = 360;
  const scale = Math.min(PREVIEW / room.w, PREVIEW / room.h);
  const pw = room.w * scale;
  const ph = room.h * scale;

  const insertables = CATALOG.filter((c) => c.kind !== "room");

  function handleAdd() {
    if (!addId || !room) return;
    // Drop at room center, attached to the cluster's groupId.
    addFromCatalog(addId, room.x + room.w / 2, room.y + room.h / 2, { groupId: room.groupId });
    setAddId("");
    toast.success("Added to cluster");
  }

  function handleSavePreset() {
    if (!room) return;
    const r = saveClusterPreset(room.id);
    if (r) toast.success(`Saved preset — ${r.count} components. Future "${room.label}" drops will use this layout.`);
  }

  function handleResetPreset() {
    if (!room) return;
    clearClusterPreset(room.type);
    toast.success("Reverted to catalog default for future drops.");
  }

  function handleSelectInCanvas(id: string) {
    select([id]);
    onOpenChange(false);
  }

  function onChildPointerDown(e: React.PointerEvent<SVGGElement>, ch: typeof children[number]) {
    e.stopPropagation();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setDragging({ id: ch.id, startX: e.clientX, startY: e.clientY, origX: ch.x, origY: ch.y });
  }
  function onChildPointerMove(e: React.PointerEvent<SVGGElement>) {
    if (!dragging) return;
    const dxCm = (e.clientX - dragging.startX) / scale;
    const dyCm = (e.clientY - dragging.startY) / scale;
    updateItem(dragging.id, { x: dragging.origX + dxCm, y: dragging.origY + dyCm });
  }
  function onChildPointerUp(e: React.PointerEvent<SVGGElement>) {
    if (!dragging) return;
    const moved = Math.hypot(e.clientX - dragging.startX, e.clientY - dragging.startY);
    const id = dragging.id;
    setDragging(null);
    if (moved < 3) handleSelectInCanvas(id);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Edit cluster · {room.label}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-[auto_1fr] gap-4">
          {/* Preview */}
          <div className="border border-border rounded-md bg-background p-3 flex flex-col items-center">
            <svg width={pw} height={ph} viewBox={`0 0 ${room.w} ${room.h}`} className="border border-border/60 rounded">
              <rect width={room.w} height={room.h} fill={room.color ?? "hsl(var(--muted))"} opacity={0.5} />
              {children.map((ch) => (
                <g
                  key={ch.id}
                  transform={`translate(${ch.x - room.x}, ${ch.y - room.y}) rotate(${ch.rotation}, ${ch.w / 2}, ${ch.h / 2})`}
                  onPointerDown={(e) => onChildPointerDown(e, ch)}
                  onPointerMove={onChildPointerMove}
                  onPointerUp={onChildPointerUp}
                  style={{ cursor: dragging?.id === ch.id ? "grabbing" : "grab", touchAction: "none" }}
                >
                  <ItemShape item={ch} />
                </g>
              ))}
            </svg>
            <div className="text-[11px] text-muted-foreground mt-2">
              Room: {cmToFtIn(room.w)} × {cmToFtIn(room.h)} — click an item to focus it on canvas
            </div>
          </div>

          {/* Children list */}
          <div className="flex flex-col gap-2 max-h-[480px] overflow-y-auto pr-1">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Add component</Label>
                <Select value={addId} onValueChange={setAddId}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {insertables.map((c) => (
                      <SelectItem key={c.id} value={c.id} className="text-xs">{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button size="sm" className="h-8 gap-1" disabled={!addId} onClick={handleAdd}>
                <Plus className="w-3.5 h-3.5" /> Add
              </Button>
            </div>

            <div className="border-t border-border pt-2 space-y-1.5">
              {children.length === 0 && (
                <div className="text-xs text-muted-foreground py-4 text-center">No components in this cluster yet.</div>
              )}
              {children.map((ch) => {
                const localX = ch.x - room.x;
                const localY = ch.y - room.y;
                return (
                  <div key={ch.id} className="rounded border border-border bg-background/60 p-2 space-y-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs font-medium truncate">{ch.label}</div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          title="Rotate 15°"
                          onClick={() => updateItem(ch.id, { rotation: (ch.rotation + 15) % 360 })}
                        >
                          <RotateCw className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0 text-destructive"
                          title="Delete"
                          onClick={() => removeItem(ch.id)}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-1.5">
                      <NumField label="X" value={localX} onChange={(v) => updateItem(ch.id, { x: room.x + v })} />
                      <NumField label="Y" value={localY} onChange={(v) => updateItem(ch.id, { y: room.y + v })} />
                      <NumField label="W" value={ch.w} onChange={(v) => updateItem(ch.id, { w: Math.max(10, v) })} />
                      <NumField label="H" value={ch.h} onChange={(v) => updateItem(ch.id, { h: Math.max(10, v) })} />
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {cmToFtIn(localX)} , {cmToFtIn(localY)} — {cmToFtIn(ch.w)} × {cmToFtIn(ch.h)} — {Math.round(ch.rotation)}°
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter className="flex !justify-between gap-2">
          <Button variant="outline" size="sm" onClick={handleResetPreset} title="Forget the saved preset for this room type">
            Reset preset
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>Done</Button>
            <Button size="sm" className="gap-1" onClick={handleSavePreset}>
              <Save className="w-3.5 h-3.5" /> Save as preset
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NumField({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <div>
      <Label className="text-[9px] uppercase tracking-wider text-muted-foreground">{label} (cm)</Label>
      <Input
        type="number"
        value={Math.round(value)}
        className="h-7 text-xs"
        onChange={(e) => onChange(Number(e.target.value) || 0)}
      />
    </div>
  );
}